import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { GroupProvider } from './context/GroupContext';
import ProtectedRoute from './components/ProtectedRoute';

import LandingPage from './pages/LandingPage';
import Workflow from './pages/Workflow';
import Dashboard from './pages/Dashboard';
import SupportDashboard from './pages/support/SupportDashboard';

import Chatbot from './pages/Chatbot';
import EmbedPage from './pages/EmbedPage';
import ProactiveFQHC from './pages/ProactiveFQHC';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import VerifyMFA from './pages/VerifyMFA';
import AcceptInvite from './pages/AcceptInvite';

import DemoLayout from './pages/demo/DemoLayout';
import DemoHome from './pages/demo/DemoHome';
import {
  DemoAIAgents,
  DemoAIBasicSettings,
  DemoAIScripts,
  DemoAIKnowledge,
  DemoAITesting,
} from './pages/demo/DemoCustomerAIPages';
import DemoScriptEditor from './pages/demo/DemoScriptEditor';
import DemoCreateAI from './pages/demo/DemoCreateAI';
import DemoDeploy from './pages/demo/DemoDeploy';
import DemoLogs from './pages/demo/DemoLogs';
import DemoInsights from './pages/demo/DemoInsights';
import DemoSettings from './pages/demo/DemoSettings';
import DemoCustomers from './pages/demo/DemoCustomers';
import DemoCalendar from './pages/demo/DemoCalendar';
import DemoAnalyticsReports from './pages/demo/DemoAnalyticsReports';
import DemoAnalyticsSetup from './pages/demo/DemoAnalyticsSetup';
import DemoSiteLayout from './pages/demo/site/DemoSiteLayout';
import DemoSiteLanding from './pages/demo/site/DemoSiteLanding';
import DemoSiteConversationalAI from './pages/demo/site/DemoSiteConversationalAI';
import DemoSiteSolutions from './pages/demo/site/DemoSiteSolutions';
import DemoSiteSolution from './pages/demo/site/DemoSiteSolution';
import DemoSiteHotelFrontDesk from './pages/demo/site/DemoSiteHotelFrontDesk';
import DemoSiteBookDemo from './pages/demo/site/DemoSiteBookDemo';
import DemoSiteTeammate from './pages/demo/site/DemoSiteTeammate';
import DemoSiteOrbiter from './pages/demo/site/DemoSiteOrbiter';
import DemoSiteApiUsers from './pages/demo/site/DemoSiteApiUsers';
import DemoSiteApiDev from './pages/demo/site/DemoSiteApiDev';
import VoiceLab from './pages/VoiceLab';
import P3TestBench from './pages/P3TestBench';
import SmsOptIn from './pages/SmsOptIn';
import P3Signup from './pages/P3Signup';
import P3App from './pages/P3App';
import DemoSitePricing from './pages/demo/site/DemoSitePricing';
import DemoSiteCompany from './pages/demo/site/DemoSiteCompany';
import DemoSiteLegal from './pages/demo/site/DemoSiteLegal';
import privacyContent from './pages/demo/site/privacyContent';
import termsContent from './pages/demo/site/termsContent';
import DemoComingSoon from './pages/demo/DemoComingSoon';

import OpinionServiceLogin from './pages/OpinionServiceLogin';
import OpinionServiceDashboard from './pages/OpinionServiceDashboard';
import OpinionScriptBuilder from './pages/OpinionScriptBuilder';
import OpinionLogs from './pages/OpinionLogs';
import ContactsImport from './pages/ContactsImport';

const AppRouter = () => {
  return (
    <AuthProvider>
      <GroupProvider>
      <Router>
        <Routes>
          {/* Public routes — root now serves the fresh road marketing site */}
          <Route path="/" element={<Navigate to="/site" replace />} />
          <Route path="/landing" element={<Navigate to="/site" replace />} />
          <Route path="/classic-landing" element={<LandingPage />} />
          <Route path="/embed" element={<EmbedPage />} />
          <Route path="/proactive-fqhc" element={<ProactiveFQHC />} />
          <Route path="/login" element={<Login />} />
          <Route path="/voice-lab" element={<VoiceLab />} />
          <Route path="/p3-testbench" element={<P3TestBench />} />
          <Route path="/sms-updates" element={<SmsOptIn />} />
          <Route path="/p3-signup" element={<P3Signup />} />
          <Route path="/p3-login" element={<P3Signup key="login" mode="login" />} />
          <Route path="/p3-verify" element={<P3Signup key="verify" mode="verify" />} />
          <Route path="/p3-forgot" element={<P3Signup key="forgot" mode="forgot" />} />
          <Route path="/p3-reset" element={<P3Signup key="reset" mode="reset" />} />
          <Route path="/p3" element={<P3App />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-mfa" element={<VerifyMFA />} />

          {/* New "fresh road" marketing site (same design language as dashboard) */}
          <Route path="/site" element={<DemoSiteLayout />}>
            <Route index element={<DemoSiteLanding />} />
            <Route path="solutions" element={<DemoSiteSolutions />} />
            <Route path="solutions/hotel-front-desk" element={<DemoSiteHotelFrontDesk />} />
            <Route path="solutions/:slug" element={<DemoSiteSolution />} />
            <Route path="book-a-demo" element={<DemoSiteBookDemo />} />
            <Route path="conversational-ai" element={<DemoSiteConversationalAI />} />
            {/* Old product URLs, kept so existing links and any indexed
                pages land on the merged page instead of 404-ing. */}
            <Route path="customer-ai" element={<Navigate to="/site/conversational-ai" replace />} />
            <Route path="customer-ai-platform" element={<Navigate to="/site/conversational-ai" replace />} />
            <Route path="teammate" element={<DemoSiteTeammate />} />
            <Route path="orbiter" element={<DemoSiteOrbiter />} />
            <Route path="api" element={<DemoSiteApiUsers />} />
            <Route path="developers" element={<DemoSiteApiDev />} />
            <Route path="pricing" element={<DemoSitePricing />} />
            <Route path="company" element={<DemoSiteCompany />} />
            <Route path="privacy" element={<DemoSiteLegal doc={privacyContent} />} />
            <Route path="terms" element={<DemoSiteLegal doc={termsContent} />} />
          </Route>

          {/* Accept group invite - page is public so invitees can see "Log in to accept" if needed */}
          <Route path="/accept-invite" element={<AcceptInvite />} />

          {/* Opinion Service - password protected */}
          <Route path="/opinion-service-login" element={<OpinionServiceLogin />} />
          <Route path="/opinion-service" element={<OpinionServiceDashboard />} />
          <Route path="/opinion-scripts" element={<OpinionScriptBuilder />} />
          <Route path="/opinion-logs" element={<OpinionLogs />} />
          <Route path="/opinion-contacts" element={<ContactsImport />} />

          {/* Main dashboard — new "fresh road" UI */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DemoLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DemoHome />} />
            <Route path="customer-ai" element={<Navigate to="/dashboard/customer-ai/agents" replace />} />
            <Route path="customer-ai/create" element={<DemoCreateAI />} />
            <Route path="customer-ai/agents" element={<DemoAIAgents />} />
            <Route path="customer-ai/phone-numbers" element={<DemoAIBasicSettings />} />
            <Route path="customer-ai/scripts" element={<DemoAIScripts />} />
            <Route path="customer-ai/scripts/:scriptId" element={<DemoScriptEditor />} />
            <Route path="customer-ai/knowledge" element={<DemoAIKnowledge />} />
            <Route path="customer-ai/testing" element={<DemoAITesting />} />
            <Route path="customer-ai/deploy" element={<DemoDeploy />} />
            <Route path="customers" element={<Navigate to="/dashboard/customers/crm" replace />} />
            <Route path="customers/crm" element={<DemoCustomers />} />
            <Route path="customers/calendar" element={<DemoCalendar />} />
            <Route path="logs" element={<DemoLogs />} />
            <Route path="insights" element={<DemoInsights />} />
            <Route path="analysis-reports" element={<DemoAnalyticsReports />} />
            <Route path="analytics-setup" element={<DemoAnalyticsSetup />} />
            <Route path="settings" element={<DemoSettings />} />
            <Route path="*" element={<DemoComingSoon />} />
          </Route>

          {/* Support desk — standalone phone console */}
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <SupportDashboard />
              </ProtectedRoute>
            }
          />

          {/* Old dashboard, moved to legacy URL */}
          <Route
            path="/legacy"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Old demo links → new dashboard */}
          <Route path="/demo/*" element={<Navigate to="/dashboard" replace />} />

          {/* Health dashboard - clone of main dashboard */}
          <Route
            path="/health"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Configure is done on Inbound/Outbound pages only; redirect old configure links to dashboard */}
          <Route path="/configure/:id" element={<Navigate to="/" replace />} />

          {/* Redirect create-agent to Agents (Deploy) with create mode */}
          <Route
            path="/create-agent"
            element={<Navigate to={{ pathname: '/', search: '?scripts=create' }} replace />}
          />

          <Route
            path="/workflow"
            element={
              <ProtectedRoute requireStaff>
                <Workflow />
              </ProtectedRoute>
            }
          />

          {/* Chatbot pages - protected */}
          <Route
            path="/chatbot"
            element={
              <ProtectedRoute requireStaff>
                <Chatbot />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chatbot/:scriptId"
            element={
              <ProtectedRoute requireStaff>
                <Chatbot />
              </ProtectedRoute>
            }
          />

          {/* Redirect any unknown routes to dashboard */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      </GroupProvider>
    </AuthProvider>
  );
};

export default AppRouter;
